import { useState } from "react";
import {
  HotelDashboardHeader,
  HotelSummarySection,
  HotelRevenueOverviewSection,
  HotelRankingsSection,
  HotelBreakdownsSection,
  HotelRevenueStreamsSection,
  getHotelSummaryCards,
  getHotelTrendData,
  getHotelStarRatingBreakdown,
  getHotelUserTypeBreakdown,
} from "../../../components/reportDashboard/hotels/hotels";
import {
  useHotelBreakdowns,
  useHotelRankings,
  useHotelRevenueStreams,
  useHotelRevenueTrends,
  useHotelSummaryMetrics,
} from "../../../hooks/useReportDashboard";
import { getDateRangeFromFilter } from "../../../utils/reportDashboard/reportDashboardUtils";
import { getErrorMessage } from "../../../utils/error";

export function Hotels() {
  const [timeRange, setTimeRange] = useState("monthly");
  const dateRange = getDateRangeFromFilter(timeRange);

  const {
    data: summaryData,
    isLoading: summaryLoading,
    isError: summaryError,
    error: summaryErrObj,
    refetch: summaryRefetch,
  } = useHotelSummaryMetrics(dateRange);

  const {
    data: trendsData,
    isLoading: trendsLoading,
    isError: trendsError,
    error: trendsErrObj,
    refetch: trendsRefetch,
  } = useHotelRevenueTrends(dateRange);

  const {
    data: streamsData,
    isLoading: streamsLoading,
    isError: streamsError,
    error: streamsErrObj,
    refetch: streamsRefetch,
  } = useHotelRevenueStreams(dateRange);

  const {
    data: breakdownsData,
    isLoading: breakdownsLoading,
    isError: breakdownsError,
    error: breakdownsErrObj,
    refetch: breakdownsRefetch,
  } = useHotelBreakdowns(dateRange);

  const {
    data: rankingsData,
    isLoading: rankingsLoading,
    isError: rankingsError,
    error: rankingsErrObj,
    refetch: rankingsRefetch,
  } = useHotelRankings(dateRange);

  const summaryState = {
    loading: summaryLoading,
    error: summaryError,
    message: getErrorMessage(summaryErrObj),
    onRetry: () => summaryRefetch(),
  };

  const trendsState = {
    loading: trendsLoading,
    error: trendsError,
    message: getErrorMessage(trendsErrObj),
    onRetry: () => trendsRefetch(),
  };

  const streamsState = {
    loading: streamsLoading,
    error: streamsError,
    message: getErrorMessage(streamsErrObj),
    onRetry: () => streamsRefetch(),
  };

  const breakdownsState = {
    loading: breakdownsLoading,
    error: breakdownsError,
    message: getErrorMessage(breakdownsErrObj),
    onRetry: () => breakdownsRefetch(),
  };

  const rankingsState = {
    loading: rankingsLoading,
    error: rankingsError,
    message: getErrorMessage(rankingsErrObj),
    onRetry: () => rankingsRefetch(),
  };

  const summaryCards = getHotelSummaryCards(summaryData?.data?.summaryMetrics);
  const monthlyTrend = getHotelTrendData(trendsData?.data?.revenueTrends);
  const starRatingBreakdown = getHotelStarRatingBreakdown(breakdownsData);
  const revenueByUserType = getHotelUserTypeBreakdown(breakdownsData);
  const revenueBySupplier = breakdownsData?.data?.revenueBySupplier || [];
  const topDestinations = rankingsData?.data?.topDestinations || [];
  const topPartners = rankingsData?.data?.topPartners || [];
  const revenueStreams = streamsData?.data?.revenueStreams || [];

  return (
    <div className="space-y-6">
      <HotelDashboardHeader
        timeRange={timeRange}
        onTimeRangeChange={setTimeRange}
      />

      <HotelSummarySection state={summaryState} summaryCards={summaryCards} />

      <HotelRevenueOverviewSection
        trendsState={trendsState}
        breakdownsState={breakdownsState}
        monthlyTrend={monthlyTrend}
        starRatingBreakdown={starRatingBreakdown}
      />

      <HotelRankingsSection
        state={rankingsState}
        topDestinations={topDestinations}
        topPartners={topPartners}
      />

      <HotelBreakdownsSection
        state={breakdownsState}
        revenueByUserType={revenueByUserType}
        revenueBySupplier={revenueBySupplier}
      />

      <HotelRevenueStreamsSection
        state={streamsState}
        revenueStreams={revenueStreams}
      />
    </div>
  );
}
